/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/// <reference types="requirejs" />
import h from 'apmui/util/createElement';

declare global {
    /* eslint-disable-next-line @typescript-eslint/interface-name-prefix */
    interface Window {
        require: Require;
    }
}

/* Extract SubResource Integrity hash from url and add integrity attribute to tag */
function addIntegrityAttribute(elem: HTMLScriptElement | HTMLLinkElement, url: string) {
    const m = /#(sha\d{3}-[A-Za-z0-9\/+=]+)/.exec(url);
    if (m) {
        elem.crossOrigin = 'anonymous';
        elem.integrity = m[1];
    }
}

/* Apply extra configuration for alameda, requirejs or compatible AMD loaders */
if (typeof window.require !== 'undefined' && 'config' in window.require) {
    window.require.config({
        onNodeCreated: (script, _conf, _name, url) => addIntegrityAttribute(script, url),
    });
}

/**
 * import() implementation
 *
 * XXX: If the AMD implementation is alameda, we can optimize loadScript to the following:
 *   const loadScript = (src: string) => require([src]);
 * However, we can only detect that at runtime.
 */
export function loadScript(src: string) {
    return new Promise((resolve, reject) => {
        try {
            window.require([src], resolve, reject);
        } catch (err) {
            /*
             * Throw error at next tick, when the .catch handler is already defined.
             * https://bugs.chromium.org/p/chromium/issues/detail?id=465666
             */
            Promise.resolve().then(() => reject(err));
        }
    });
}

export function loadStyle(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const style = h('link', {
            href: src,
            rel: 'stylesheet',
            type: 'text/css',
            onLoad: () => resolve(),
            onError: () => reject(new Error(`Load failed: '${src}'`)),
        });
        addIntegrityAttribute(style, src);
        /* null check override: current script tag is in <head> */
        document.head!.appendChild(style);
    });
}
